import { useState, useEffect } from 'react';
import { ChartBarIcon } from '@heroicons/react/24/solid';

// Helper function to get all consent decisions from localStorage
const getAllConsentDecisions = () => {
  const decisions = [];
  
  // Loop through all localStorage keys to find consent decisions
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.includes('CookieConsent')) {
      const value = localStorage.getItem(key);
      const timestamp = key.includes('_timestamp') 
        ? parseInt(localStorage.getItem(key))
        : Date.now(); // Fallback to current time
      
      decisions.push({
        decision: value,
        timestamp: timestamp,
        source: key
      });
    }
  }
  
  return decisions;
};

// Track new consent decisions
export const trackConsentDecision = (decision) => {
  const timestamp = Date.now();
  
  // Store the decision
  localStorage.setItem('giveTechCookieConsent', decision);
  
  // Also store with timestamp for analytics
  localStorage.setItem(`consent_${timestamp}`, decision);
  localStorage.setItem(`consent_${timestamp}_timestamp`, timestamp.toString());
  
  console.log(`📊 Consent tracked: ${decision} at ${new Date(timestamp).toLocaleString()}`);
};

export default function ConsentAnalytics() {
  const [consentStats, setConsentStats] = useState({
    accepted: 0,
    rejected: 0,
    total: 0,
    acceptanceRate: 0,
    recentDecisions: []
  });

  useEffect(() => {
    calculateRealConsentStats();
    
    // Listen for new consent decisions
    const handleStorageChange = () => {
      calculateRealConsentStats();
    };
    
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const calculateRealConsentStats = () => {
    const decisions = getAllConsentDecisions();
    
    // Filter unique decisions (by user - in a real app, you'd have user IDs)
    const uniqueDecisions = decisions.filter((decision, index, self) =>
      index === self.findIndex(d => 
        d.source === decision.source && 
        !d.source.includes('_timestamp')
      )
    );

    const accepted = uniqueDecisions.filter(d => d.decision === 'accepted').length;
    const rejected = uniqueDecisions.filter(d => d.decision === 'rejected').length;
    const total = accepted + rejected;
    const acceptanceRate = total > 0 ? Math.round((accepted / total) * 100) : 0;

    // Get recent decisions (last 7 days)
    const oneWeekAgo = Date.now() - (7 * 24 * 60 * 60 * 1000);
    const recentDecisions = decisions
      .filter(d => d.timestamp >= oneWeekAgo)
      .sort((a, b) => b.timestamp - a.timestamp)
      .slice(0, 10);

    setConsentStats({
      accepted,
      rejected,
      total,
      acceptanceRate,
      recentDecisions
    });
  };

  const refreshStats = () => {
    calculateRealConsentStats();
  };

  const clearTestData = () => {
    // Remove all test consent data
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.includes('consent_')) {
        keysToRemove.push(key);
      }
    }
    
    keysToRemove.forEach(key => localStorage.removeItem(key));
    calculateRealConsentStats();
  };

  const addTestData = () => {
    // Add some test data to see how it works
    const testDecisions = [
      { decision: 'accepted', hoursAgo: 2 },
      { decision: 'accepted', hoursAgo: 5 },
      { decision: 'rejected', hoursAgo: 8 },
      { decision: 'accepted', hoursAgo: 24 },
      { decision: 'rejected', hoursAgo: 48 }
    ];

    testDecisions.forEach(test => {
      const timestamp = Date.now() - (test.hoursAgo * 60 * 60 * 1000);
      localStorage.setItem(`consent_${timestamp}`, test.decision);
      localStorage.setItem(`consent_${timestamp}_timestamp`, timestamp.toString());
    });

    calculateRealConsentStats();
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold text-gray-800 dark:text-white flex items-center">
          <ChartBarIcon className="w-6 h-6 mr-2 text-orange-500" />
          Cookie Consent Analytics
        </h2>
        <div className="flex gap-2">
          <button
            onClick={addTestData}
            className="px-3 py-1 text-sm bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
          >
            Add Test Data
          </button>
          <button
            onClick={clearTestData}
            className="px-3 py-1 text-sm bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors"
          >
            Clear Test
          </button>
          <button
            onClick={refreshStats}
            className="px-3 py-1 text-sm bg-orange-500 text-white rounded hover:bg-orange-600 transition-colors"
          >
            Refresh
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
          <div className="text-2xl font-bold text-green-600 dark:text-green-400">
            {consentStats.accepted}
          </div>
          <div className="text-sm text-green-600 dark:text-green-400 font-medium">
            Accepted
          </div>
          <div className="text-xs text-green-500 dark:text-green-300 mt-1">
            Real users who accepted
          </div>
        </div>

        <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg">
          <div className="text-2xl font-bold text-red-600 dark:text-red-400">
            {consentStats.rejected}
          </div>
          <div className="text-sm text-red-600 dark:text-red-400 font-medium">
            Rejected
          </div>
          <div className="text-xs text-red-500 dark:text-red-300 mt-1">
            Real users who rejected
          </div>
        </div>

        <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
          <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
            {consentStats.total}
          </div>
          <div className="text-sm text-blue-600 dark:text-blue-400 font-medium">
            Total Decisions
          </div>
          <div className="text-xs text-blue-500 dark:text-blue-300 mt-1">
            Users who made a choice
          </div>
        </div>

        <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg">
          <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
            {consentStats.acceptanceRate}%
          </div>
          <div className="text-sm text-purple-600 dark:text-purple-400 font-medium">
            Acceptance Rate
          </div>
          <div className="text-xs text-purple-500 dark:text-purple-300 mt-1">
            Real percentage
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="mb-4">
        <div className="flex justify-between text-sm text-gray-600 dark:text-gray-300 mb-2">
          <span>Acceptance Rate</span>
          <span>{consentStats.acceptanceRate}%</span>
        </div>
        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
          <div 
            className="bg-green-500 h-3 rounded-full transition-all duration-500"
            style={{ width: `${consentStats.acceptanceRate}%` }}
          ></div>
        </div>
      </div>

      {/* Recent Decisions */}
      {consentStats.recentDecisions.length > 0 && (
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-3">
            Recent Consent Decisions
          </h3>
          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {consentStats.recentDecisions.map((decision, index) => (
                <div key={index} className="flex justify-between items-center text-sm">
                  <span className={`px-2 py-1 rounded ${
                    decision.decision === 'accepted' 
                      ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                      : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                  }`}>
                    {decision.decision.toUpperCase()}
                  </span>
                  <span className="text-gray-600 dark:text-gray-400">
                    {new Date(decision.timestamp).toLocaleString()}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Additional Insights */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
        <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
          <div className="font-medium text-gray-800 dark:text-gray-200">
            Analytics Coverage
          </div>
          <div className="text-gray-600 dark:text-gray-400">
            {consentStats.accepted} users ({consentStats.acceptanceRate}%) are being tracked
          </div>
        </div>
        
        <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
          <div className="font-medium text-gray-800 dark:text-gray-200">
            Data Collection
          </div>
          <div className="text-gray-600 dark:text-gray-400">
            {consentStats.rejected} users opted out of analytics
          </div>
        </div>
      </div>

      {/* Data Source Info */}
      <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
        <p className="text-sm text-blue-700 dark:text-blue-300">
          ✅ <strong>Real Data:</strong> Tracking actual user consent decisions from localStorage. 
          {consentStats.total === 0 && ' No consent decisions recorded yet.'}
        </p>
      </div>
    </div>
  );
}